import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { nanoid } from '@reduxjs/toolkit'
import { OptionsObject, SnackbarKey, VariantType, useSnackbar } from 'notistack'
import { useCallback } from 'react'
import store from 'app/store'

export type EnqSnackBar = {
  message: string
  variant: VariantType | undefined
  autoHideDuration?: OptionsObject['autoHideDuration']
}

export function useNotification() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const action = useCallback(
    (key: SnackbarKey | undefined) => (
      <IconButton
        onClick={() => {
          closeSnackbar(key)
        }}
      >
        <CloseIcon />
      </IconButton>
    ),
    [closeSnackbar]
  )

  const sendNotification = useCallback(
    (configuration: EnqSnackBar) => {
      if (configuration?.message) {
        const maintenanceActive = store.getState().maintenance.isActive

        if (maintenanceActive && configuration.variant === 'error') {
          return
        }

        enqueueSnackbar(configuration.message, {
          key: nanoid(),
          variant: configuration.variant,
          autoHideDuration: configuration.autoHideDuration ?? 5000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          action,
        })
      }
    },
    [action, enqueueSnackbar]
  )

  const errorNotification = useCallback(
    (msg?: string) =>
      sendNotification({
        message: msg ?? 'Something went wrong!',
        variant: 'error',
      }),
    [sendNotification]
  )

  const successNotification = useCallback(
    (msg?: string) =>
      sendNotification({
        message: msg ?? 'Success!',
        variant: 'success',
      }),
    [sendNotification]
  )

  return { sendNotification, errorNotification, successNotification }
}
